/**
 * View component for /user/login
 *
 * On successful login this component forwards the user back to referrer
 * or to the root if there is no referrer.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
// import primary libraries
import { useLocation, Link } from 'react-router-dom';
import apiUtils from '../../../global/utils/api';

import UserLayout from '../components/UserLayout.jsx';
import UserLoginFlow from '../components/UserLoginFlow.jsx';
import useMarketingContent from '../../../global/utils/useMarketingContent';


const UserLogin = () => {
  const location = useLocation();
  const { redirect } = apiUtils.objectFromQueryString(location.search);
  const resetLink = redirect ? `/user/login?redirect=${redirect}` : '/user/login';

  // NOTE: For devs doing local testing, if you're running into CORS errors
  //       or not getting the marketing content to show up, try modifying the
  //       useMarketingContent HEAD error handling or having it load the
  //       marketing content from a local running static-marketing server
  const { mobile, urls, errors, headerId, footerId, setSlotError } = useMarketingContent(
    'login'
    , ['header', 'footer', 'background', 'expansion']
  );

  return (
    <UserLayout
      title='Sign in'
      className="w-screen h-screen overflow-hidden"
    >
      <div className='sm:w-full mx-auto flex flex-col flex-nowrap h-full max-h-full justify-center md:justify-between relative'>
        {/* Marketing Background Image */}
        {urls.background && !mobile && !errors.background && (
          <iframe
            src={urls.background}
            title="Marketing background"
            aria-hidden="true"
            sandbox="allow-scripts allow-same-origin"
            className="hidden md:block w-full h-full absolute inset-0 -z-10 overflow-hidden border-0"
            onError={() => setSlotError('background')}
          />
        )}
        {/* Fallback background - shown if error or no URL */}
        {(errors.background || !urls.background) && !mobile && (
          <div className="block bg-neutral-pri w-full h-full absolute inset-0 -z-10"></div>
        )}

        {/* Header */}
        <div id={headerId} className="flex flex-grow-0 flex-shrink-0 w-full">
          {urls.header && !errors.header && (
            <iframe
              src={urls.header}
              title="Marketing header"
              sandbox="allow-scripts allow-same-origin"
              className="h-full w-full max-h-full max-w-full flex-grow flex-shrink border-0"
              onError={() => setSlotError('header')}
            />
          )}
          {(errors.header || !urls.header) && (
            <div className="h-0 w-full"></div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-nowrap justify-center items-center">
          {/* Image Carousel - Removed for now in favor of the central controls + marketing background.
              To restore: add 'splash' to the useMarketingContent slots array above.
          {urls.splash && !errors.splash && (
            <div className="flex-1 p-3 items-center justify-center hidden md:flex max-w-[70%]">
              <iframe
                src={urls.splash}
                title="Marketing splash image"
                sandbox="allow-scripts allow-same-origin"
                className="w-full h-full border-0"
                onError={() => setSlotError('splash')}
              />
            </div>
          )}
          {(errors.splash || !urls.splash) && (
            <p className="text-center flex justify-center items-center w-full h-full">
              Access your Simulations Plus products by signing in through S+ Cloud
            </p>
          )} */}

          <div className="bg-neutral-pri flex flex-col flex-nowrap flex-grow flex-shrink items-center justify-start max-w-full md:w-[550px] md:max-w-[550px] max-h-full gap-2 p-4 rounded-lg overflow-auto">
            <UserLoginFlow
              resetLink={resetLink}
              loginRedirect={redirect}
              wrapperClass='w-full'
            />

            {/* About S+ Cloud expansion section */}
            {urls.expansion && !errors.expansion && (
              <div className='w-full text-xs mt-2 flex-shrink-0 text-neutral-sec'>
                <iframe
                  src={urls.expansion}
                  title="Marketing expansion"
                  id="loginMarketingExpansion"
                  sandbox="allow-scripts allow-same-origin"
                  className="w-full block min-h-0 max-h-[80px] sm:max-h-[150px] md:max-h-[200px] overflow-y-auto border-0"
                  onError={() => setSlotError('expansion')}
                />
              </div>
            )}
            {(!urls.expansion || errors.expansion) && (
              // Fallback empty content if expansion fails
              <div className="w-full h-0"></div>
            )}

            <div className="flex flex-col flex-nowrap min-h-0 w-full gap-2 text-neutral-sec">
              <p className='text-center mt-4 text-sm flex-grow-0 flex-shrink-0'>
                Don't have an account?
                <Link
                  to={{
                    pathname: '/user/register'
                    , search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : undefined
                    , state: location.state
                  }}
                  className='mx-2'
                >
                  Sign up
                </Link>
              </p>

              {/* Terms + Privacy Policy */}
              <div className="flex-grow-0 flex-shrink-0 flex gap-2 justify-center p-4">
                <a href="https://www.simulations-plus.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Terms of Use</a>
                <span>•</span>
                <a href="https://www.simulations-plus.com/privacy-notice/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!mobile && (
          <div id={footerId} className="hidden md:flex flex-shrink-0">
            {urls.footer && !errors.footer && (
              <iframe
                src={urls.footer}
                title="Marketing footer"
                sandbox="allow-scripts allow-same-origin"
                className="max-h-full max-w-full flex-grow flex-shrink border-0"
                onError={() => setSlotError('footer')}
              />
            )}
            {(errors.footer || !urls.footer) && (
              <div className="h-0 w-full"></div>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  )
}

export default UserLogin;
